import { z } from "zod";
// The contract of the provider-health route (CHT-770, Phase 13.4):
//
//   GET /staff/operations/provider-health     capability `operations.jobs.read` (supervisor, administrator)
//
// The staff diagnostics page (CHT-772) draws this answer. It holds a status, counts, an error rate, closed failure codes and LINKS to the
// domain record of a failure (a payment request, a background intent, an agent run). It holds no secret, no header, no payload, no vendor text,
// no customer id and no name. It is NOT proof of a payment, a request or a confirmation: the canonical state is in the record the link points at.
//
// Every shape is STRICT. This package cannot import the application: the tuples below repeat its registry (`provider-health.ts`), and a parity test
// in `apps/api` fails when one differs.
export const STAFF_PROVIDER_HEALTH_PATH = "/staff/operations/provider-health";
export const staffProviderIds = [
    "stream",
    "trigger",
    "razorpay",
    "google",
    "expo",
    "model",
    "resend",
    "supabase",
];
export const staffProviderModes = ["off", "not_configured", "fake", "live"];
export const staffProviderStatuses = [
    "NOT_CONFIGURED",
    "FAKE",
    "UNKNOWN",
    "OK",
    "DEGRADED",
    "DOWN",
];
export const staffProviderLinkTypes = [
    "payment_request",
    "background_intent",
    "agent_run",
    "conversation",
    "import_job",
];
// A response instant: the API passes a `Date` and the wire holds an ISO text; the typed client reads it back as a `Date` (as the jobs contract does).
const instant = z.union([
    z.date(),
    z.iso.datetime({ offset: true }).transform((value) => new Date(value)),
]);
const code = z.string().regex(/^[A-Za-z0-9_]{1,64}$/);
const id = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/);
export const staffProviderFailureSchema = z.strictObject({
    at: instant,
    code,
    link: z.strictObject({ type: z.enum(staffProviderLinkTypes), id }).nullable(),
    /** The Trigger.dev run id of a hosted run, or `null`. */
    runRef: id.nullable(),
});
export const staffProviderHealthEntrySchema = z.strictObject({
    provider: z.enum(staffProviderIds),
    mode: z.enum(staffProviderModes),
    status: z.enum(staffProviderStatuses),
    windowMinutes: z.number().int().min(1),
    calls: z.number().int().min(0).nullable(),
    failures: z.number().int().min(0).nullable(),
    errorRate: z.number().min(0).max(1).nullable(),
    lastFailureAt: instant.nullable(),
    recentFailures: z.array(staffProviderFailureSchema).max(5),
    /** A closed code: `FAKE_NOT_EVIDENCE`, `DB_PROBE_ONLY`, `QUEUE_STALE`, `PAUSED`, `SOURCE_FAILED`. Never free text. */
    note: z
        .string()
        .regex(/^[A-Z_]{1,40}$/)
        .nullable(),
    /** Refusals of unauthenticated callers in the window. A SECURITY count outside `calls`, `failures` and `errorRate`. `null`: not counted. */
    securityRefusals: z.number().int().min(0).nullable(),
});
export const staffProviderHealthSchema = z.strictObject({
    generatedAt: instant,
    windowMinutes: z.number().int().min(1),
    providers: z.array(staffProviderHealthEntrySchema).length(staffProviderIds.length),
});
